import {
  calculateEloChange,
  calculateKFactor,
  matchCertainty,
  ELO_BASE_K_FACTOR,
  ELO_POLICY_CURRENT,
  ELO_POLICY_LEGACY,
  ELO_POLICY_V2,
} from './eloUtils';
import { detectGameType, getGameTypeDescription } from './gameTypeUtils';
import {
  isBalancedDoubles,
  isBalancedMixed,
  getScorelineNudge,
  getCompetitiveResultScore,
  clampCompetitiveEloDelta,
} from './gameRatingUtils';
import { isSessionMensDoublesEloExemptLineup } from '@badminton/matchmaking-core';
import { clearCourtMatchTimer } from './matchTimerUtils';

const DEFAULT_ELO = 1200;

/**
 * Per-player prematch Elo payload for doubles (captured when a court goes in progress).
 * @typedef {Object} PrematchEloSnapshot
 * @property {number} levels_elo_rating
 * @property {number} mixed_elo_rating
 * @property {number} levels_games_played
 * @property {number} mixed_games_played
 */

/**
 * Build prematch snapshots for four doubles players from current `playersById`.
 * @param {string[]} team1Ids
 * @param {string[]} team2Ids
 * @param {Object} playersById
 * @returns {Object<string, PrematchEloSnapshot>}
 */
export function buildPrematchEloSnapshots(team1Ids, team2Ids, playersById) {
  const ids = [...(team1Ids || []), ...(team2Ids || [])];
  const out = {};
  for (const id of ids) {
    const p = playersById[id];
    if (!p) continue;
    out[id] = {
      levels_elo_rating:
        p.levels_elo_rating == null ? DEFAULT_ELO : Number(p.levels_elo_rating),
      mixed_elo_rating:
        p.mixed_elo_rating == null ? DEFAULT_ELO : Number(p.mixed_elo_rating),
      levels_games_played: Number(p.levels_games_played) || 0,
      mixed_games_played: Number(p.mixed_games_played) || 0,
    };
  }
  return out;
}

/**
 * True when an in-progress doubles court (2v2) should get or refresh prematch snapshots.
 * @param {Object} court
 * @param {Object} playersById
 */
export function courtNeedsPrematchEloSnapshot(court, playersById) {
  if (!court?.inProgress) return false;
  const t1 = court.team1 || [];
  const t2 = court.team2 || [];
  if (t1.length !== 2 || t2.length !== 2) return false;
  const ids = [...t1, ...t2];
  for (const id of ids) {
    if (!playersById[id]) return false;
  }
  const snap = court.eloSnapshots;
  if (!snap || typeof snap !== 'object') return true;
  for (const id of ids) {
    if (!snap[id]) return true;
  }
  return false;
}

/**
 * Merge prematch ratings + discipline games into a player clone for doubles Elo math.
 * Falls back to live `player` when no snapshot (legacy / tests).
 */
function applyPrematchEloForDoublesCalculation(player, eloSnapshots) {
  if (!player?.id) return player;
  const snap = eloSnapshots?.[player.id];
  if (!snap) return player;
  return {
    ...player,
    levels_elo_rating:
      snap.levels_elo_rating == null
        ? (player.levels_elo_rating ?? DEFAULT_ELO)
        : Number(snap.levels_elo_rating),
    mixed_elo_rating:
      snap.mixed_elo_rating == null
        ? (player.mixed_elo_rating ?? DEFAULT_ELO)
        : Number(snap.mixed_elo_rating),
    levels_games_played:
      snap.levels_games_played == null
        ? (player.levels_games_played || 0)
        : Number(snap.levels_games_played) || 0,
    mixed_games_played:
      snap.mixed_games_played == null
        ? (player.mixed_games_played || 0)
        : Number(snap.mixed_games_played) || 0,
  };
}

/**
 * Use stored prematch snapshots when they cover this lineup; otherwise build from `playersById` (fallback).
 * @param {Object[]} team1Players
 * @param {Object[]} team2Players
 * @param {Object} court
 * @param {Object} playersById
 */
function resolveEloSnapshotsForLineup(team1Players, team2Players, court, playersById) {
  const t1ids = (team1Players || []).map((p) => p.id);
  const t2ids = (team2Players || []).map((p) => p.id);
  const ids = [...t1ids, ...t2ids];
  if (ids.length !== 4 || ids.some((id) => !id)) return court.eloSnapshots || {};
  const existing = court.eloSnapshots || {};
  const complete = ids.every((id) => existing[id] && playersById[id]);
  if (complete) {
    const picked = {};
    ids.forEach((id) => {
      picked[id] = existing[id];
    });
    return picked;
  }
  return buildPrematchEloSnapshots(t1ids, t2ids, playersById);
}

function isGameBalanced(team1Players, team2Players, { isMixed, isLevels }) {
  if (isMixed) {
    return isBalancedMixed(team1Players, team2Players);
  }
  if (isLevels) {
    return isBalancedDoubles(team1Players, team2Players);
  }
  return false;
}

function ratingOfDoublesPlayer(player, isMixed) {
  return isMixed ? (player.mixed_elo_rating || 1200) : (player.levels_elo_rating || 1200);
}

function gamesOfDoublesPlayer(player, isMixed) {
  return isMixed ? (player.mixed_games_played || 0) : (player.levels_games_played || 0);
}

/**
 * Team-based ELO calculation.
 * @param {object} [options]
 * @param {string} [options.eloPolicy]
 * @param {number} [options.team1Score]
 * @param {number} [options.team2Score]
 */
export function calculateTeamBasedElo(
  team1Players,
  team2Players,
  team1Won,
  scorelineNudge = 0,
  isMixed = false,
  options = {}
) {
  const eloPolicy = options.eloPolicy || ELO_POLICY_CURRENT;
  const team1Score = options.team1Score;
  const team2Score = options.team2Score;
  const useLegacy = eloPolicy === ELO_POLICY_LEGACY;

  const team1AvgRating =
    team1Players.reduce((sum, player) => sum + ratingOfDoublesPlayer(player, isMixed), 0) /
    team1Players.length;
  const team2AvgRating =
    team2Players.reduce((sum, player) => sum + ratingOfDoublesPlayer(player, isMixed), 0) /
    team2Players.length;

  let team1ActualScore;
  let team2ActualScore;
  let allowLoserGain = false;
  let team1Nudge = 0;
  let team2Nudge = 0;

  if (useLegacy) {
    team1ActualScore = team1Won ? 1 : 0;
    team2ActualScore = team1Won ? 0 : 1;
    let nudge = scorelineNudge || 0;
    if (team1Score != null && team2Score != null) {
      const scoreDiff = Math.abs((Number(team1Score) || 0) - (Number(team2Score) || 0));
      const balanced = isGameBalanced(team1Players, team2Players, {
        isMixed,
        isLevels: !isMixed,
      });
      nudge = balanced ? getScorelineNudge(scoreDiff) : 0;
    }
    team1Nudge = team1Won ? nudge : -nudge;
    team2Nudge = -team1Nudge;
  } else {
    const result = getCompetitiveResultScore(team1Score, team2Score);
    allowLoserGain = result.allowLoserGain;
    team1ActualScore = team1Won ? result.winnerScore : 1 - result.winnerScore;
    team2ActualScore = 1 - team1ActualScore;
  }

  const team1BaseDelta = calculateEloChange(
    team1AvgRating,
    team2AvgRating,
    team1ActualScore,
    ELO_BASE_K_FACTOR,
    1
  );
  const team2BaseDelta = calculateEloChange(
    team2AvgRating,
    team1AvgRating,
    team2ActualScore,
    ELO_BASE_K_FACTOR,
    1
  );

  const mapTeam = (players, opponents, baseDelta, nudge, won) =>
    players.map((player, index) => {
      const playerGames = gamesOfDoublesPlayer(player, isMixed);
      const playerKFactor = calculateKFactor(playerGames, false, eloPolicy);
      const partner = players[index === 0 ? 1 : 0];
      const othersGames = [
        ...(partner ? [gamesOfDoublesPlayer(partner, isMixed)] : []),
        ...(opponents || []).map((p) => gamesOfDoublesPlayer(p, isMixed)),
      ];
      const certainty = useLegacy ? 1 : matchCertainty(othersGames, eloPolicy);
      let playerDelta = Math.round(
        baseDelta * (playerKFactor / ELO_BASE_K_FACTOR) * certainty
      );
      const appliedNudge = useLegacy ? nudge : 0;
      if (!useLegacy) {
        playerDelta = clampCompetitiveEloDelta(playerDelta, { won, allowLoserGain });
      }
      const currentRating = ratingOfDoublesPlayer(player, isMixed);
      const total = playerDelta + appliedNudge;
      return {
        ...player,
        [isMixed ? 'mixed_elo_rating' : 'levels_elo_rating']: currentRating + total,
        [isMixed ? 'mixed_games_played' : 'levels_games_played']: playerGames + 1,
        delta: total,
        baseDelta: playerDelta,
        nudge: appliedNudge,
      };
    });

  return [
    ...mapTeam(team1Players, team2Players, team1BaseDelta, team1Nudge, team1Won),
    ...mapTeam(team2Players, team1Players, team2BaseDelta, team2Nudge, !team1Won),
  ];
}

/**
 * @typedef {Object} Player
 * @property {string} id - Player's unique identifier
 * @property {string} name - Player's name
 * @property {number} elo - Player's ELO rating
 * @property {string} gender - Player's gender
 * @property {string} clubId - Player's club ID
 */

/**
 * @typedef {Object} Court
 * @property {number} courtNumber - Court identifier
 * @property {Player[]} team1 - First team players
 * @property {Player[]} team2 - Second team players
 * @property {boolean} inProgress - Whether a game is currently in progress
 * @property {string|null} winner - Winner of the match (null if not finished)
 * @property {string|null} score - Match score (null if not finished)
 */

/**
 * @typedef {Object} Club
 * @property {string} id - Club's unique identifier
 * @property {string} name - Club's name
 * @property {number} strength_rating - Club's strength rating
 */

/**
 * Assigns players to courts based on the number of available courts
 * This is a utility function used in testing and simulations.
 * @param {Player[]} activePlayers - List of active players to assign
 * @param {number} numberOfCourts - Number of courts available
 * @returns {Object} Object containing success status, courts array, and waiting list
 */
export const assignCourtsToPlayers = (activePlayers, numberOfCourts) => {
  if (activePlayers.length < 2) {
    return { 
      success: false, 
      error: 'Need at least 2 active players to assign courts',
      courts: [],
      waitingList: []
    };
  }
  
  // Shuffle and assign players to courts
  const shuffled = [...activePlayers].sort(() => Math.random() - 0.5);
  const courts = [];
  
  for (let i = 0; i < numberOfCourts; i++) {
    const team1 = shuffled.splice(0, 2);
    const team2 = shuffled.splice(0, 2);
    if (team1.length === 2 && team2.length === 2) {
      courts.push({
        courtNumber: i + 1,
        team1,
        team2,
        inProgress: false,
        winner: null
      });
    }
  }
  
  return { 
    success: true, 
    courts, 
    waitingList: shuffled 
  };
};

/**
 * Removes a player from a specific court and adds them to the waiting list
 * @param {Court[]} currentCourts - Current court assignments
 * @param {Player[]} currentWaitingList - Current waiting list
 * @param {Player} player - Player to remove
 * @param {number} team - Team number (1 or 2)
 * @param {number} courtNumber - Court number to remove player from
 * @returns {Object} Object containing updated courts and waiting list
 */
export const removePlayerFromCourt = (currentCourts, currentWaitingList, player, team, courtNumber) => {
  const updated = [...currentCourts];
  const courtIndex = updated.findIndex(court => court.courtNumber === courtNumber);
  
  if (courtIndex === -1) {
    return { courts: updated, waitingList: currentWaitingList };
  }
  
  const court = updated[courtIndex];
  const teamKey = team === 1 ? 'team1' : 'team2';
  court[teamKey] = court[teamKey].filter(p => p.id !== player.id);

  if (court.gameCreation) {
    if (court.gameCreation.origin === 'matchmaking' || court.gameCreation.origin === 'scheduled') {
      court.gameCreation = { ...court.gameCreation, isAltered: true };
    }
  } else {
    court.gameCreation = { origin: 'authoritative', algorithm: null, isAltered: false };
  }
  
  // Reset inProgress if court is no longer full (less than 4 players total)
  const totalPlayers = court.team1.length + court.team2.length;
  if (totalPlayers < 4) {
    clearCourtMatchTimer(court);
  }
  
  // Add player to waiting list
  const newWaitingList = [...currentWaitingList, player];
  
  return { 
    courts: updated, 
    waitingList: newWaitingList 
  };
};

/**
 * Removes a court and moves all its players to the waiting list
 * @param {Court[]} currentCourts - Current court assignments
 * @param {Player[]} currentWaitingList - Current waiting list
 * @param {number} courtIndex - Index of the court to remove
 * @param {{ waitlistFront?: boolean }} [options] - Peg Board: return players to the front of the queue
 * @returns {Object} Object containing updated courts and waiting list
 */
export const removeCourt = (currentCourts, currentWaitingList, courtIndex, { waitlistFront = false } = {}) => {
  const updated = [...currentCourts];
  const removedCourt = updated.splice(courtIndex, 1)[0];
  
  if (!removedCourt) {
    return { courts: updated, waitingList: currentWaitingList };
  }
  
  const returning = [...(removedCourt.team1 || []), ...(removedCourt.team2 || [])];
  const remaining = (currentWaitingList || []).filter((id) => !returning.includes(id));
  const newWaitingList = waitlistFront
    ? [...returning, ...remaining]
    : [...currentWaitingList, ...returning];
  
  // Keep court numbers fixed - don't renumber
  // The removed court will be replaced with an empty court at the same position
  const emptyCourt = {
    courtNumber: removedCourt.courtNumber,
    team1: [],
    team2: [],
    inProgress: false,
    winner: null,
    score: null
  };
  
  // Insert empty court at the same position
  updated.splice(courtIndex, 0, emptyCourt);
  
  return { 
    courts: updated, 
    waitingList: newWaitingList 
  };
};

/**
 * Keep a fixed number of upcoming-queue rows: trim to slotCount, pad with empty slots, renumber courtNumber.
 * Avoids UI jank when rotation/promotion shortens nextAssignments before async refill.
 *
 * @param {Array} remainingMatches - Matches left after promoting the head(s) of the queue
 * @param {number} slotCount - Host-configured upcoming slot count (e.g. numberOfUpcomingCourts)
 * @param {(index: number) => Object} createEmptySlot - Factory for an empty upcoming match object
 */
export function padUpcomingQueueToSlotCount(remainingMatches, slotCount, createEmptySlot) {
  const cap = Math.max(0, slotCount);
  const capped = Array.isArray(remainingMatches) ? remainingMatches.slice(0, cap) : [];
  const out = [...capped];
  while (out.length < cap) {
    out.push(createEmptySlot(out.length));
  }
  return normalizeNextUpCourtNumbers(out);
}

const defaultEmptyUpcomingSlot = (idx) => ({
  courtNumber: idx,
  team1: [],
  team2: [],
  inProgress: false,
  gameCreation: { origin: null, algorithm: null, isAltered: false, scheduledGameId: null },
});

/**
 * After promoting the first upcoming game, shift the remaining queue toward
 * slot 0 (same as finish_session_match / rotateAndRefillCourts), pad back to
 * the host cap, and force court_number = index so SEAT / targeted fetch keys match.
 */
export function consumeUpcomingQueueHead(
  nextAssignments,
  slotCount,
  createEmptySlot = defaultEmptyUpcomingSlot,
) {
  const rest = Array.isArray(nextAssignments) ? nextAssignments.slice(1) : [];
  return padUpcomingQueueToSlotCount(rest, slotCount, createEmptySlot).map((slot, idx) => ({
    ...slot,
    courtNumber: idx,
  }));
}

/** 0-based court_number for next_up rows — matches DB / setNextUpSlotCmd. */
export function normalizeNextUpCourtNumbers(slots) {
  if (!Array.isArray(slots)) return [];
  return slots.map((slot, idx) => ({
    ...slot,
    courtNumber: slot?.courtNumber ?? idx,
  }));
}

/**
 * Order-independent roster fingerprint (team1 ∪ team2 sort). Used when comparing
 * local court snapshots to detect host edits before rotate (e.g. DnD).
 */
export function courtRosterSig(court) {
  if (!court) return '|';
  const t1 = [...(court.team1 || [])].map(String).sort().join(',');
  const t2 = [...(court.team2 || [])].map(String).sort().join(',');
  return `${t1}|${t2}`;
}

/**
 * Updates match result for a specific court and calculates ELO changes
 * @param {Court[]} currentCourts - Current court assignments
 * @param {number} courtIndex - Index of the court to update
 * @param {string} winner - Winner of the match ('team1' or 'team2')
 * @param {Object} score - Match score object with team1Score and team2Score
 * @param {string} clubId - Club ID for ELO calculations
 * @param {Object} playersById - Object mapping player IDs to player objects
 * @returns {Court[]} Updated court assignments with ELO changes
 */
export const updateMatchResult = async (currentCourts, courtIndex, winner, score, clubId, playersById, playerWaitMetrics = {}) => {
  const updated = [...currentCourts];
  if (!updated[courtIndex]) {
    return updated;
  }

  const court = updated[courtIndex];
  
  // Ensure both teams have 2 players for ELO calculation
  if (court.team1.length !== 2 || court.team2.length !== 2) {
    // If teams are incomplete, just update the result without ELO calculation
    updated[courtIndex] = {
      ...court,
      winner,
      score,
      inProgress: false
    };
    return updated;
  }

  // Use current team composition for ELO calculation
  // Only fall back to original teams if current teams are incomplete
  const currentTeam1 = court.team1.map(id => playersById[id]).filter(Boolean);
  const currentTeam2 = court.team2.map(id => playersById[id]).filter(Boolean);
  
  // Check if we have valid current teams
  if (currentTeam1.length !== 2 || currentTeam2.length !== 2) {
    // Fall back to original teams if current teams are incomplete
    const originalTeam1 = (court.originalTeam1 || court.team1).map(id => playersById[id]).filter(Boolean);
    const originalTeam2 = (court.originalTeam2 || court.team2).map(id => playersById[id]).filter(Boolean);
    
    // If original teams are also incomplete, skip ELO calculation
    if (originalTeam1.length !== 2 || originalTeam2.length !== 2) {
      updated[courtIndex] = {
        ...court,
        winner,
        score,
        inProgress: false
      };
      return updated;
    }
    
    // Use original teams for ELO calculation
    const gameType = detectGameType(originalTeam1, originalTeam2);
    const gameTypeId = gameType.id;
    const isMixed = gameTypeId === 1;
    const isLevels = gameTypeId === 2 || gameTypeId === 3 || gameTypeId === 4 || gameTypeId === 5 || gameTypeId === 7;
    const isUnbalanced = gameTypeId === 6;

    // ELO-exempt: session mens doubles with at least one eligible female
    const isEloExempt = isSessionMensDoublesEloExemptLineup(
      (court.originalTeam1 || court.team1 || []).map((p) => (typeof p === 'string' ? p : p?.id)).filter(Boolean),
      (court.originalTeam2 || court.team2 || []).map((p) => (typeof p === 'string' ? p : p?.id)).filter(Boolean),
      playerWaitMetrics,
      playersById,
    );

    // For unbalanced games (or session mens doubles), do not update any ELOs
    if (isUnbalanced || isEloExempt) {
      const gameTypeName = isEloExempt ? 'Session mens doubles' : await getGameTypeDescription(gameTypeId);
      updated[courtIndex] = {
        ...court,
        winner,
        score,
        inProgress: false,
        eloSnapshots: undefined,
        unbalancedInfo: {
          gameTypeName,
          message: isEloExempt
            ? `Session mens doubles — club ratings not updated. Results still recorded!`
            : `Game completed as "${gameTypeName}" - ELO ratings not updated due to unbalanced teams. Results still recorded!`
        }
      };
      return updated;
    }

    // Calculate ELO changes for all players using original team composition
    const [player1, player2] = originalTeam1;
    const [player3, player4] = originalTeam2;

    const team1Won = winner === 'team1';
    const team1Score = Number(score?.team1Score) || 0;
    const team2Score = Number(score?.team2Score) || 0;

    const eloSnapshots = resolveEloSnapshotsForLineup(
      originalTeam1,
      originalTeam2,
      court,
      playersById
    );
    const playerSnapshots = [player1, player2, player3, player4].map((p) =>
      applyPrematchEloForDoublesCalculation(p, eloSnapshots)
    );

    // Calculate new ELOs using team-based ELO calculation
    const team1Players = [playerSnapshots[0], playerSnapshots[1]];
    const team2Players = [playerSnapshots[2], playerSnapshots[3]];

    const allResults = calculateTeamBasedElo(
      team1Players,
      team2Players,
      team1Won,
      0,
      isMixed,
      { eloPolicy: ELO_POLICY_V2, team1Score, team2Score }
    );
    
    const [player1Result, player2Result, player3Result, player4Result] = allResults;

    // Update only the relevant ELO field based on game type
    if (isMixed) {
      // Only update mixed ELO for mixed games
      player1.mixed_elo_rating = player1Result.mixed_elo_rating;
      player2.mixed_elo_rating = player2Result.mixed_elo_rating;
      player3.mixed_elo_rating = player3Result.mixed_elo_rating;
      player4.mixed_elo_rating = player4Result.mixed_elo_rating;
      player1.mixed_games_played = player1Result.mixed_games_played;
      player2.mixed_games_played = player2Result.mixed_games_played;
      player3.mixed_games_played = player3Result.mixed_games_played;
      player4.mixed_games_played = player4Result.mixed_games_played;
    } else if (isLevels) {
      // Only update levels ELO for levels games
      player1.levels_elo_rating = player1Result.levels_elo_rating;
      player2.levels_elo_rating = player2Result.levels_elo_rating;
      player3.levels_elo_rating = player3Result.levels_elo_rating;
      player4.levels_elo_rating = player4Result.levels_elo_rating;
      player1.levels_games_played = player1Result.levels_games_played;
      player2.levels_games_played = player2Result.levels_games_played;
      player3.levels_games_played = player3Result.levels_games_played;
      player4.levels_games_played = player4Result.levels_games_played;
    }
    // Note: Unbalanced games (gameTypeId === 6) are handled above and return early
    // This section only handles balanced games (mixed, levels, singles)

    // Update games played for all players
    player1.club_games_played = player1Result.club_games_played;
    player2.club_games_played = player2Result.club_games_played;
    player3.club_games_played = player3Result.club_games_played;
    player4.club_games_played = player4Result.club_games_played;

    // ELO history records will be created separately in the service layer
    // This function only handles ELO calculations and court updates

    // Store eloDeltas with baseDelta and nudge breakdown for UI display
    const eloDeltas = {};
    [player1Result, player2Result, player3Result, player4Result].forEach((result, idx) => {
      const playerId = [player1, player2, player3, player4][idx].id;
      eloDeltas[playerId] = {
        total: result.delta,
        baseDelta: result.baseDelta,
        nudge: result.nudge
      };
    });

    // Update the court with results
    updated[courtIndex] = {
      ...court,
      winner,
      score,
      inProgress: false,
      eloDeltas,
      eloSnapshots: undefined,
    };

    return updated;
  } else {
    // Use current team composition for ELO calculation
  const gameType = detectGameType(currentTeam1, currentTeam2);
  const gameTypeId = gameType.id;
  const isMixed = gameTypeId === 1;
  const isLevels = gameTypeId === 2 || gameTypeId === 3 || gameTypeId === 4 || gameTypeId === 5 || gameTypeId === 7;
  const isUnbalanced = gameTypeId === 6;

  // ELO-exempt: session mens doubles with at least one eligible female
  const isEloExempt2 = isSessionMensDoublesEloExemptLineup(
    court.team1 || [],
    court.team2 || [],
    playerWaitMetrics,
    playersById,
  );

  // For unbalanced games (or session mens doubles), do not update any ELOs
  if (isUnbalanced || isEloExempt2) {
    const gameTypeName2 = isEloExempt2 ? 'Session mens doubles' : await getGameTypeDescription(gameTypeId);
    updated[courtIndex] = {
      ...court,
      winner,
      score,
      inProgress: false,
      eloSnapshots: undefined,
      unbalancedInfo: {
        gameTypeName: gameTypeName2,
        message: isEloExempt2
          ? `Session mens doubles — club ratings not updated. Results still recorded!`
          : `Game completed as "${gameTypeName2}" - ELO ratings not updated due to unbalanced teams. Results still recorded!`
      }
    };
    return updated;
  }

  // Calculate ELO changes for all players using current team composition
  const [player1, player2] = currentTeam1;
  const [player3, player4] = currentTeam2;

  const team1Won = winner === 'team1';
  const team1Score = Number(score?.team1Score) || 0;
  const team2Score = Number(score?.team2Score) || 0;

  const eloSnapshots = resolveEloSnapshotsForLineup(
    currentTeam1,
    currentTeam2,
    court,
    playersById
  );
  const playerSnapshots = [player1, player2, player3, player4].map((p) =>
    applyPrematchEloForDoublesCalculation(p, eloSnapshots)
  );

  // Calculate new ELOs using team-based ELO calculation
  const team1Players = [playerSnapshots[0], playerSnapshots[1]];
  const team2Players = [playerSnapshots[2], playerSnapshots[3]];

  const allResults = calculateTeamBasedElo(
    team1Players,
    team2Players,
    team1Won,
    0,
    isMixed,
    { eloPolicy: ELO_POLICY_V2, team1Score, team2Score }
  );
  
  const [player1Result, player2Result, player3Result, player4Result] = allResults;

  // Update only the relevant ELO field based on game type
  if (isMixed) {
    // Only update mixed ELO for mixed games
    player1.mixed_elo_rating = player1Result.mixed_elo_rating;
    player2.mixed_elo_rating = player2Result.mixed_elo_rating;
    player3.mixed_elo_rating = player3Result.mixed_elo_rating;
    player4.mixed_elo_rating = player4Result.mixed_elo_rating;
    player1.mixed_games_played = player1Result.mixed_games_played;
    player2.mixed_games_played = player2Result.mixed_games_played;
    player3.mixed_games_played = player3Result.mixed_games_played;
    player4.mixed_games_played = player4Result.mixed_games_played;
  } else if (isLevels) {
    // Only update levels ELO for levels games
    player1.levels_elo_rating = player1Result.levels_elo_rating;
    player2.levels_elo_rating = player2Result.levels_elo_rating;
    player3.levels_elo_rating = player3Result.levels_elo_rating;
    player4.levels_elo_rating = player4Result.levels_elo_rating;
    player1.levels_games_played = player1Result.levels_games_played;
    player2.levels_games_played = player2Result.levels_games_played;
    player3.levels_games_played = player3Result.levels_games_played;
    player4.levels_games_played = player4Result.levels_games_played;
  }
  // Note: Unbalanced games (gameTypeId === 6) are handled above and return early
  // This section only handles balanced games (mixed, levels, singles)

  // Update games played for all players
  player1.club_games_played = player1Result.club_games_played;
  player2.club_games_played = player2Result.club_games_played;
  player3.club_games_played = player3Result.club_games_played;
  player4.club_games_played = player4Result.club_games_played;

  // ELO history records will be created separately in the service layer
  // This function only handles ELO calculations and court updates

  // Store eloDeltas with baseDelta and nudge breakdown for UI display
  const eloDeltas = {};
  [player1Result, player2Result, player3Result, player4Result].forEach((result, idx) => {
    const playerId = [player1, player2, player3, player4][idx].id;
    eloDeltas[playerId] = {
      total: result.delta,
      baseDelta: result.baseDelta,
      nudge: result.nudge
    };
  });

  // Update the court with results
  updated[courtIndex] = {
    ...court,
    winner,
    score,
    inProgress: false,
    eloDeltas,
    eloSnapshots: undefined,
  };

  return updated;
  }
};

/**
 * Adjusts the number of courts by the given increment
 * @param {number} currentCount - Current number of courts
 * @param {number} increment - Amount to increment/decrement by
 * @returns {number} New court count (minimum 1)
 */
export const adjustCourtCount = (currentCount, increment) => {
  return Math.max(1, currentCount + increment);
};

/**
 * Adjusts the number of waiting courts by the given increment
 * @param {number} currentCount - Current number of waiting courts
 * @param {number} increment - Amount to increment/decrement by
 * @returns {number} New waiting court count (minimum 0)
 */
export const adjustWaitingCourtCount = (currentCount, increment) => {
  return Math.max(0, currentCount + increment);
}; 